import { api } from './client'

/** ADR-019 (Equal Point, card 3): client di `ProjectsController` (`/api/projects`), progetti del ramo Studio. */
export type ProjectKind = 'progetto' | 'canone' | 'revenue_share' | 'commissioni' | 'leadgen'
export type ProjectStatus = 'in_attesa_firma' | 'attivo' | 'chiuso' | 'scaduto'

export const PROJECT_KINDS: { value: ProjectKind; label: string }[] = [
  { value: 'progetto', label: 'Progetto' },
  { value: 'canone', label: 'Canone' },
  { value: 'revenue_share', label: 'Revenue share' },
  { value: 'commissioni', label: 'Commissioni' },
  { value: 'leadgen', label: 'Lead generation' },
]

export const PROJECT_STATUSES: { value: ProjectStatus; label: string; tone: 'success' | 'warning' | 'neutral' | 'error' }[] = [
  { value: 'in_attesa_firma', label: 'In attesa di firma', tone: 'warning' },
  { value: 'attivo', label: 'Attivo', tone: 'success' },
  { value: 'chiuso', label: 'Chiuso', tone: 'neutral' },
  { value: 'scaduto', label: 'Scaduto', tone: 'error' },
]

export interface ProjectRow {
  id: number
  customerId: number
  customerName: string | null
  businessLine: string
  title: string
  kind: ProjectKind
  status: ProjectStatus
  setupAmount: number | null
  monthlyAmount: number | null
  percentage: number | null
  durationMonths: number | null
  startDate: string | null
  endDate: string | null
  source: 'manuale' | 'contratti'
  sourceRef: string | null
}

export interface ProjectList {
  total: number
  page: number
  pageSize: number
  activeCount: number
  monthlyTotal: number
  items: ProjectRow[]
}

export interface ProjectDetail extends Omit<ProjectRow, 'customerName'> {
  customer: { id: number; name: string; email: string | null } | null
  notes: string | null
  createdAt: string
  updatedAt: string | null
}

export interface ProjectRequest {
  customerId: number
  title: string
  kind: ProjectKind
  status: ProjectStatus
  setupAmount: number | null
  monthlyAmount: number | null
  percentage: number | null
  durationMonths: number | null
  startDate: string | null
  endDate: string | null
  notes: string | null
}

export const projectsApi = {
  list: (p: { customerId?: number; status?: ProjectStatus | ''; kind?: ProjectKind | ''; q?: string; page?: number; pageSize?: number } = {}) => {
    const s = new URLSearchParams()
    if (p.customerId) s.set('customerId', String(p.customerId))
    if (p.status) s.set('status', p.status)
    if (p.kind) s.set('kind', p.kind)
    if (p.q) s.set('q', p.q)
    s.set('page', String(p.page ?? 1))
    s.set('pageSize', String(p.pageSize ?? 50))
    return api<ProjectList>(`/api/projects?${s.toString()}`)
  },
  get: (id: number) => api<ProjectDetail>(`/api/projects/${id}`),
  create: (data: ProjectRequest) => api<{ id: number }>('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: ProjectRequest) => api<void>(`/api/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: number) => api<void>(`/api/projects/${id}`, { method: 'DELETE' }),
}

export const kindLabel = (k: ProjectKind) => PROJECT_KINDS.find(x => x.value === k)?.label ?? k
export const statusOf = (s: ProjectStatus) => PROJECT_STATUSES.find(x => x.value === s) ?? { value: s, label: s, tone: 'neutral' as const }

export const eur = (n: number | null) => (n === null ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }))
/** Una data senza ora (YYYY-MM-DD) letta come giorno locale. */
export const day = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('it-IT') : '—')
/** Un istante UTC (created_at) mostrato nel giorno italiano. */
export const dayOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '—')

/** Fine calcolata come sul server (DateOnly.AddMonths: il giorno si ferma all'ultimo del mese). */
export function endFrom(start: string, months: string): string {
  const m = Number(months)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !Number.isInteger(m) || m < 1) return ''
  const [y, mo, d] = start.split('-').map(Number) as [number, number, number]
  const total = (mo - 1) + m
  const ty = y + Math.floor(total / 12)
  const tm = total % 12
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate()
  return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

/** Le condizioni economiche in una riga: una tantum, canone, percentuale. */
export function economics(p: { setupAmount: number | null; monthlyAmount: number | null; percentage: number | null }): string {
  const parts = [
    p.setupAmount !== null ? `${eur(p.setupAmount)} una tantum` : null,
    p.monthlyAmount !== null ? `${eur(p.monthlyAmount)}/mese` : null,
    p.percentage !== null ? `${p.percentage.toLocaleString('it-IT')}%` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

/**
 * «1.300,50», «1300,5», «1300.5» o «1,300.50» → 1300.5; vuoto → null; testo non numerico → NaN (la scheda lo blocca
 * prima di salvare). Con punto e virgola insieme il separatore dei decimali è quello che viene per ultimo.
 */
export function parseAmount(s: string): number | null {
  const t = s.trim().replace(/[\s€]/g, '')
  if (!t) return null
  if (t.includes(',') && t.includes('.')) {
    return t.lastIndexOf(',') > t.lastIndexOf('.')
      ? Number(t.replace(/\./g, '').replace(',', '.'))
      : Number(t.replace(/,/g, ''))
  }
  if (t.includes(',')) return Number(t.replace(',', '.'))
  // «1.300» senza virgola è un migliaio scritto all'italiana, non 1,3.
  if (/^\d{1,3}(\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ''))
  return Number(t)
}
